/**
 * capabilities.ts
 *
 * Microkernel Authorization & Capability Contracts.
 * Strictly defines principals, roles, capabilities, and session modes.
 */

export type Principal = {
  readonly id: string;
  readonly displayName: string;
  readonly authenticated: boolean;
  readonly sessionId: string;
  readonly deviceOwnership: 'host-owned' | 'user-owned' | 'managed';
};

export type Role = 'sovereign-owner' | 'node-admin' | 'member';

export type Capability =
  | 'workspace.manage'
  | 'plugin.configure'
  | 'inventory.read'
  | 'inventory.receive'
  | 'inventory.transfer'
  | 'inventory.adjust'
  | 'inventory.writeoff'
  | 'inventory.quarantine'
  | 'inventory.import'
  | 'inventory.export'
  | 'audit.export'
  | 'security.wipe';

export type SessionMode =
  | { readonly type: 'authenticated'; readonly principalId: string }
  | { readonly type: 'role-preview'; readonly previewRole: Role; readonly sourcePrincipalId: string };

export type AuthorizationContext = {
  readonly principal: Principal;
  readonly roles: readonly Role[];
  readonly capabilities: readonly Capability[];
  readonly sessionMode: SessionMode;
};

export const ROLE_CAPABILITY_MAP: Record<Role, readonly Capability[]> = {
  'sovereign-owner': [
    'workspace.manage',
    'plugin.configure',
    'inventory.read',
    'inventory.receive',
    'inventory.transfer',
    'inventory.adjust',
    'inventory.writeoff',
    'inventory.quarantine',
    'inventory.import',
    'inventory.export',
    'audit.export',
    'security.wipe',
  ],
  'node-admin': [
    'inventory.read',
    'inventory.receive',
    'inventory.transfer',
    'inventory.adjust',
    'inventory.quarantine',
    'inventory.import',
    'inventory.export',
    'audit.export',
  ],
  member: [
    'inventory.read',
    'inventory.receive',
    'inventory.transfer',
  ],
};
